import { lazy, Suspense, useEffect, useState } from "react";
import { NavLink } from "react-router-dom";

import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import { EMAIL } from "config/constants";

const EmailIcon = lazy(() => import("@mui/icons-material/Email"));
const KeyboardArrowUpIcon = lazy(
  () => import("@mui/icons-material/KeyboardArrowUp")
);
const WhatsAppIcon = lazy(() => import("@mui/icons-material/WhatsApp"));
const InstagramIcon = lazy(() => import("@mui/icons-material/Instagram"));

const StyledFooter = styled("footer")(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.common.white,
  padding: theme.spacing(4, 2),
  position: "relative",
}));

const ScrollTop = styled("button")(({ theme }) => ({
  position: "fixed",
  bottom: theme.spacing(6),
  right: theme.spacing(4),
  backgroundColor: theme.palette.primary.dark,
  border: "none",
  borderRadius: "50%",
  width: 48,
  height: 48,
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  color: theme.palette.common.white,
  cursor: "pointer",
  zIndex: 1000,
  boxShadow: theme.shadows[4],
  "&:hover": {
    backgroundColor: theme.palette.primary.light,
  },
}));

const WhatsFloating = styled("a")(({ theme }) => ({
  position: "fixed",
  bottom: theme.spacing(14),
  right: "28px",
  backgroundColor: "#25D366",
  borderRadius: "50%",
  width: 56,
  height: 56,
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  color: theme.palette.common.white,
  textDecoration: "none",
  boxShadow: theme.shadows[4],
  zIndex: 1000,
  "&:hover": {
    backgroundColor: "#1DA851",
  },
}));

const PartnerBadge = styled("div")(({ theme }) => ({
  display: "inline-flex",
  flexDirection: "column",
  alignItems: "center",
  gap: theme.spacing(0.4),
  padding: theme.spacing(1.1, 1.75),
  borderRadius: theme.shape.borderRadius * 2,
  backgroundColor: "#F2F9FF",
  "& img": {
    display: "block",
    width: 118,
    maxWidth: "100%",
    height: "auto",
  },
}));

const PartnerLabel = styled("span")({
  fontSize: "0.6rem",
  fontWeight: 600,
  letterSpacing: "0.04em",
  textTransform: "uppercase",
  color: "#0050C3",
});

const StyledListItem = styled(ListItem)({
  paddingLeft: 0,
  paddingRight: 0,
});

const institutionalLinks = [
  { to: "/sobre-nos", label: "Sobre Nós" },
  { to: "/termos-de-uso", label: "Termos de uso" },
  { to: "/politica-de-privacidade", label: "Política de privacidade" },
];

const WHATSAPP_URL = "https://api.whatsapp.com/send?phone=5511964914546";
const INSTA_URL = "https://www.instagram.com/sistemasflex/";

function Footer() {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const toggleVisibility = () => {
      setIsVisible(window.scrollY > 64);
    };

    window.addEventListener("scroll", toggleVisibility);
    return () => window.removeEventListener("scroll", toggleVisibility);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <StyledFooter>
      <Suspense fallback={null}>
        {isVisible && (
          <ScrollTop
            onClick={scrollToTop}
            aria-label="Voltar ao topo"
            title="Voltar ao topo"
          >
            <KeyboardArrowUpIcon fontSize="large" />
          </ScrollTop>
        )}
      </Suspense>

      <WhatsFloating
        href={WHATSAPP_URL}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="WhatsApp"
        title="WhatsApp"
      >
        <Suspense fallback={null}>
          <WhatsAppIcon fontSize="large" />
        </Suspense>
      </WhatsFloating>

      <Container
        sx={{
          display: "flex",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 4,
        }}
      >
        <Box sx={{ minWidth: 200 }}>
          <Typography
            variant="h6"
            component="h4"
            gutterBottom
            sx={{
              fontSize: {
                xs: "18px", // mobile
                sm: "20px",
              },
            }}
          >
            Institucional
          </Typography>
          <List sx={{ display: "flex", flexDirection: "column", padding: 0 }}>
            {institutionalLinks.map(({ to, label }) => (
              <StyledListItem key={to}>
                <NavLink
                  to={to}
                  style={{ color: "inherit", textDecoration: "none" }}
                >
                  {label}
                </NavLink>
              </StyledListItem>
            ))}
          </List>
        </Box>

        <Box sx={{ minWidth: 200 }}>
          <Typography
            variant="h6"
            component="h4"
            sx={{
              fontSize: {
                xs: "18px", // mobile
                sm: "20px",
              },
            }}
            gutterBottom
          >
            Contato
          </Typography>
          <List sx={{ padding: 0 }}>
            <StyledListItem>
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: "inherit", textDecoration: "none" }}
              >
                (11) 96491-4546
              </a>
            </StyledListItem>
            <StyledListItem>
              <a
                href={`mailto:${EMAIL}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: "inherit", textDecoration: "none" }}
              >
                {EMAIL}
              </a>
            </StyledListItem>
          </List>

          <Typography
            sx={{
              fontSize: 15,
              marginTop: 1,
              lineHeight: 1.4,
            }}
          >
            Segunda a Sexta - 09:00h às 18:00h <br />
            Sábado - 09:00h às 13:00h
          </Typography>
        </Box>

        <Box sx={{ minWidth: 200 }}>
          <Typography
            variant="h6"
            component="h4"
            gutterBottom
            sx={{
              fontSize: {
                xs: "18px", // mobile
                sm: "20px",
              },
            }}
          >
            Parcerias
          </Typography>
          <PartnerBadge>
            <img
              src="/assets/nuvemshop-logo.png"
              alt="Nuvemshop"
              width={501}
              height={92}
              loading="lazy"
            />
            <PartnerLabel>Parceiro Oficial</PartnerLabel>
          </PartnerBadge>
        </Box>

        <Box
          className="footer-bottom"
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            minWidth: 200,
          }}
        >
          <List
            sx={{
              display: "flex",
              justifyContent: "center",
              padding: 0,
              marginBottom: 1,
            }}
          >

            <ListItem sx={{ width: "auto", padding: "0 8px" }}>
              <IconButton
                href={`mailto:${EMAIL}`}
                color="inherit"
                title="E-mail"
                aria-label="Enviar email"
              >
                <EmailIcon />
              </IconButton>
            </ListItem>

            <ListItem sx={{ width: "auto", padding: "0 8px" }}>
              <IconButton
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                color="inherit"
                title="WhatsApp"
                aria-label="Abrir WhatsApp"
              >
                <WhatsAppIcon />
              </IconButton>
            </ListItem>

            <ListItem sx={{ width: "auto", padding: "0 8px" }}>
              <IconButton
                href={INSTA_URL}
                target="_blank"
                rel="noopener noreferrer"
                color="inherit"
                title="Instagram"
                aria-label="Abrir Instagram"
              >
                <InstagramIcon />
              </IconButton>
            </ListItem>
          </List>

          <Typography
            variant="body2"
            sx={{
              textAlign: "center",
              color: "rgba(255,255,255,0.8)",
            }}
          >
            &copy; 2026 Sistemas Flex - CNPJ: 66.203.552/0001-05 <br />
            Todos os direitos reservados.
          </Typography>
        </Box>
      </Container>
    </StyledFooter>
  );
}

export default Footer;
